import React, { ReactNode } from "react";
import { InputNumber, Select, Switch } from "antd";
import useLang from "../../../hooks/useLang";
import { currencySymbol, formatMoney } from "../../../utils/formatters";
import {
	ClientMessagingOptions,
	DEFAULT_SETTING,
	MessageChannel,
} from "../../../api/client-messaging-settings";
import { MessagingSettingRow } from "./MessagingSettingRow";
import { ClientMessagingSettingsForm } from "./useClientMessagingSettings";
import { MessagingDraft, templatesFor } from "./messagingDraft";

interface MessagingSettingsSectionProps {
	form: ClientMessagingSettingsForm;
	isEditing: boolean;
}

interface SettingRow {
	key: string;
	label: string;
	content: ReactNode;
	error?: string;
}

/**
 * The client's messaging settings on Client Details: the In Transport message and its cost, the
 * delivery PIN, channel, template, and hiding the price on tickets. Read-only until the page is in
 * edit mode; the page's own Save button saves them through `form.save()`.
 *
 * Only the messaging switch shows while messaging is off — every other setting depends on it.
 * Channel and template show only where SENT sends these messages.
 */
export const MessagingSettingsSection = ({
	form,
	isEditing,
}: MessagingSettingsSectionProps) => {
	const { strings } = useLang("clientDetails");
	const copy = strings?.messaging;
	const { draft, options } = form;

	if (!draft || !options) return null;

	const yesNo = (value: boolean): string =>
		value ? strings?.yes : strings?.no;
	const channelName = (channel: MessageChannel): string =>
		copy?.channels?.[channel] ?? channel;
	const templateName = (id: string): string =>
		options.templates.find((template) => template.id === id)?.name ?? id;

	const costError = form.errors.includes("costRequired")
		? copy?.errors?.costRequired
		: undefined;
	const pinError = form.errors.includes("pinlessNeedsSent")
		? copy?.errors?.pinlessNeedsSent
		: undefined;
	const templateErrorCode = form.errors.find(
		(code) =>
			code === "noDefaultWithoutPin" || code === "templateUnavailable"
	);
	const templateError = templateErrorCode
		? copy?.errors?.[templateErrorCode]
		: undefined;

	const defaultChannelLabel = withDefault(
		copy?.defaultOption,
		channelName(options.defaultChannel)
	);
	const defaultTemplateLabel = withDefault(
		copy?.defaultOption,
		defaultTemplateName(draft, options)
	);
	const channelText = draft.messageChannel
		? channelName(draft.messageChannel)
		: defaultChannelLabel;
	const templateText = draft.messageTemplateId
		? templateName(draft.messageTemplateId)
		: defaultTemplateLabel;

	const channelOptions = [
		{ value: DEFAULT_SETTING, label: defaultChannelLabel },
		...options.channels.map((channel) => ({
			value: channel,
			label: channelName(channel),
		})),
	];
	const fittingTemplateOptions = templatesFor(draft, options).map(
		(template) => ({
			value: template.id,
			label: template.name ?? template.id,
		})
	);
	// A template withdrawn after it was assigned still shows by name, flagged by its error.
	const storedTemplateId = draft.messageTemplateId;
	const withdrawnTemplateOptions =
		storedTemplateId &&
		!fittingTemplateOptions.some(
			(option) => option.value === storedTemplateId
		)
			? [
					{
						value: storedTemplateId,
						label: templateName(storedTemplateId),
					},
			  ]
			: [];
	const templateOptions = [
		{ value: DEFAULT_SETTING, label: defaultTemplateLabel },
		...fittingTemplateOptions,
		...withdrawnTemplateOptions,
	];

	const handleChannelChange = (value: string) =>
		form.change({
			messageChannel:
				value === DEFAULT_SETTING ? null : (value as MessageChannel),
		});
	const handleTemplateChange = (value: string) =>
		form.change({
			messageTemplateId: value === DEFAULT_SETTING ? null : value,
		});

	const serviceRow: SettingRow = {
		key: "service",
		label: copy?.service,
		content: isEditing ? (
			<Switch
				className="bg-neutral-300"
				checked={draft.isMessagingEnabled}
				onChange={(checked) =>
					form.change({ isMessagingEnabled: checked })
				}
			/>
		) : (
			yesNo(draft.isMessagingEnabled)
		),
	};
	const costRow: SettingRow = {
		key: "cost",
		label: copy?.cost,
		error: costError,
		content: isEditing ? (
			<InputNumber
				min={0}
				precision={2}
				step={0.1}
				value={draft.messageCost}
				addonAfter={currencySymbol()}
				status={costError && "error"}
				onChange={(value) => form.change({ messageCost: value })}
			/>
		) : (
			formatMoney(draft.messageCost)
		),
	};
	const pinRow: SettingRow = {
		key: "deliveryPin",
		label: copy?.deliveryPin,
		error: pinError,
		content: isEditing ? (
			<Switch
				className="bg-neutral-300"
				checked={draft.requiresDeliveryPin}
				// Without SENT the PIN can't be turned off, but it can always be turned back on.
				disabled={!options.isSentEnabled && draft.requiresDeliveryPin}
				onChange={(checked) =>
					form.change({ requiresDeliveryPin: checked })
				}
			/>
		) : (
			yesNo(draft.requiresDeliveryPin)
		),
	};
	const channelRow: SettingRow = {
		key: "channel",
		label: copy?.channel,
		content: isEditing ? (
			<Select
				className="w-full max-w-xs"
				popupMatchSelectWidth={false}
				value={draft.messageChannel ?? DEFAULT_SETTING}
				options={channelOptions}
				onChange={handleChannelChange}
			/>
		) : (
			channelText
		),
	};
	const templateRow: SettingRow = {
		key: "template",
		label: copy?.template,
		error: templateError,
		content: isEditing ? (
			<Select
				className="w-full max-w-xs"
				popupMatchSelectWidth={false}
				value={draft.messageTemplateId ?? DEFAULT_SETTING}
				options={templateOptions}
				status={templateError && "error"}
				onChange={handleTemplateChange}
			/>
		) : (
			templateText
		),
	};
	const hidePriceRow: SettingRow = {
		key: "hidePrice",
		label: copy?.hidePrice,
		content: isEditing ? (
			<Switch
				className="bg-neutral-300"
				checked={draft.hideOrderPriceOnTicket}
				onChange={(checked) =>
					form.change({ hideOrderPriceOnTicket: checked })
				}
			/>
		) : (
			yesNo(draft.hideOrderPriceOnTicket)
		),
	};

	// Channel and template only matter where SENT sends. A value stored before SENT was turned off
	// still shows, so the admin can see it and reset it to the default.
	const isChannelShown =
		options.isSentEnabled || draft.messageChannel !== null;
	const isTemplateShown =
		options.isSentEnabled || draft.messageTemplateId !== null;
	const messagingRows = draft.isMessagingEnabled
		? [
				costRow,
				pinRow,
				isChannelShown && channelRow,
				isTemplateShown && templateRow,
				hidePriceRow,
		  ]
		: [];
	const rows = [serviceRow, ...messagingRows].filter(isSettingRow);

	return (
		<div>
			{rows.map((row, index) => (
				<MessagingSettingRow
					key={row.key}
					label={row.label}
					isStriped={index % 2 === 1}
					error={row.error}
				>
					{row.content}
				</MessagingSettingRow>
			))}
			{form.serverErrors.length > 0 && (
				<ul className="list-disc px-8 py-2 text-xs text-danger">
					{form.serverErrors.map((error) => (
						<li key={error}>{error}</li>
					))}
				</ul>
			)}
		</div>
	);
};

/** Drops the rows a condition switched off (`false`). */
const isSettingRow = (row: SettingRow | false): row is SettingRow =>
	row !== false;

/** "Default (SMS)" — or just "Default" when there is nothing to name. */
const withDefault = (label: string | undefined, name?: string): string => {
	const template = label ?? "{value}";
	return name
		? template.replace("{value}", name)
		: template.replace(" ({value})", "");
};

/** The default template's name, when it is one the admin can also see in the list. */
const defaultTemplateName = (
	draft: MessagingDraft,
	options: ClientMessagingOptions
): string | undefined => {
	const id = draft.requiresDeliveryPin
		? options.defaultTemplateWithPinId
		: options.defaultTemplateWithoutPinId;
	return (
		options.templates.find((template) => template.id === id)?.name ??
		undefined
	);
};
